/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/alt-text */
import { FC, MouseEvent, useMemo, useState } from "react";
import { Alert, Box, Stack, Typography, useTheme } from "@mui/material";
import type { IRoadLayoutConfig, ILuminaireLayoutConfig, ILightingClassConfig, IOptimizationConfig, IRoadwayConfig, IFamilyConfig, PortfolioData } from "types";
import { setFromURLObject } from "utils/urls";
import { Loader } from "components/Loader";
import { useInitFamilyIndexFromURL } from "./hooks/useInitFamilyIndexFromURL";
import { useSelectResultFetch } from "./hooks/useSelectResultFetch";
import { mapResults } from "./common/select-result-mapper";
import ResultsTable from "./components/table/ResultsTable";
import Header from "./components/Header";
import getStyles from "./styles";

interface IProps {
  families: { data: PortfolioData | null };
  familyConfig: IFamilyConfig;
  selectedResult: string | number;
  handleSelectResult: (result: string) => void;
  roadLayoutConfig: IRoadLayoutConfig;
  roadwayConfig: IRoadwayConfig;
  luminaireLayoutConfig: ILuminaireLayoutConfig;
  lightingClassConfig: ILightingClassConfig;
  optimizationConfig: IOptimizationConfig;
  resultFamilyIndex: number;
  setResultFamilyIndex: (index: number) => void;
}

const SelectResult: FC<IProps> = ({
  families,
  familyConfig,
  selectedResult,
  handleSelectResult,
  roadLayoutConfig,
  roadwayConfig,
  lightingClassConfig,
  luminaireLayoutConfig,
  optimizationConfig,
  resultFamilyIndex,
  setResultFamilyIndex,
}) => {
  const [tab, setTab] = useState("data");
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const { data, isLoading, error } = useSelectResultFetch({
    familyConfig,
    roadLayoutConfig,
    roadwayConfig,
    lightingClassConfig,
    luminaireLayoutConfig,
    optimizationConfig,
  });

  // @ts-ignore
  useInitFamilyIndexFromURL(families, setResultFamilyIndex);

  if (error.message !== null) {
    return (
      <Alert variant="outlined" severity="error" sx={{ m: "calc(2 * var(--rhythm))" }}>
        <Typography variant="h3" mb="var(--rhythm)">
          An error has occured
        </Typography>
        <Typography variant="body2">Error message:</Typography>
        <Box component="pre">{error.message}</Box>
      </Alert>
    );
  }

  // TODO @anis: setFromURLObject mutates config objects in place during render (anti-pattern).
  // Fixing this requires the parent (App.tsx) to expose individual setState setters for each config,
  // so that setFromURLObject can trigger proper re-renders instead of silent mutations.
  // Until then, leave as-is — moving to useEffect would break URL hydration before the first fetch.
  setFromURLObject(roadLayoutConfig);
  setFromURLObject(roadwayConfig);
  setFromURLObject(lightingClassConfig);
  setFromURLObject(luminaireLayoutConfig);
  setFromURLObject(optimizationConfig);

  const placingStyle = luminaireLayoutConfig.luminaireConfig;
  const { maintenanceFactor } = optimizationConfig;
  // @ts-ignore
  const options = data === null ? [] : mapResults(data, families, placingStyle, maintenanceFactor, lightingClassConfig);

  const handleChange = (_: MouseEvent<HTMLElement>, newTab: string) => {
    setTab(newTab);
  };

  return (
    <Stack sx={styles.container}>
      {isLoading && <Loader />}

      <Header tab={tab} onChange={handleChange} />

      <ResultsTable
        tab={tab}
        familyConfig={familyConfig}
        roadLayoutConfig={roadLayoutConfig}
        roadwayConfig={roadwayConfig}
        optimizationConfig={optimizationConfig}
        lightingClassConfig={lightingClassConfig}
        families={families}
        options={options}
        callback={handleSelectResult}
        selected={selectedResult}
        resultFamilyIndex={resultFamilyIndex}
        setResultFamilyIndex={setResultFamilyIndex}
      />
    </Stack>
  );
};

export default SelectResult;
